/**
 * Ключи устройства для секретных чатов (изменение v1.13, BS 7.14).
 *
 * У устройства три пары: подписывающий Ed25519 (личность — из него считается код
 * безопасности), identity X25519 и подписанный prekey, плюс запас одноразовых
 * prekeys. Подписывающий ключ — неизвлекаемый `CryptoKey` в IndexedDB; X25519-ключи
 * хранятся байтами: их состояние сериализуется вместе с сессией рэтчета, а WebKit
 * X25519 `CryptoKey` в IndexedDB не сохраняет вовсе. Публикуется набор один раз на
 * устройство, а дальше пополняются только одноразовые prekeys и раз в неделю меняется
 * подписанный.
 *
 * Набор публикуется фоном после входа (`publishDeviceKeysQuietly`), а не только перед
 * собственным первым секретным чатом: иначе с человеком, который сам его ни разу не
 * начинал, чат начать нельзя — у сервера нет ключей его устройства (409
 * `secret_chat_unavailable`). Перед стартом чата набор перепроверяется ещё раз.
 */
import { api, unwrap } from '@shared/api/client'
import type { SecretKeyBundlePublish, SecretKeyStatus } from '@shared/api/types'
import { toBase64 } from '@shared/crypto/bytes'
import {
  exportDhPrivate,
  generateDh,
  generateSigning,
  importDhPrivate,
  isSecretCryptoSupported,
} from '@shared/crypto/primitives'
import { signIdentity, signPrekey, type LocalIdentity } from '@shared/crypto/x3dh'
import { secretKeysRepo, type SecretIdentityRecord, type SecretPrekeyRecord } from '@shared/db'

import { withSecretLock } from './lock'

/** Сколько одноразовых prekeys держать на сервере и когда пополнять запас. */
export const ONE_TIME_PREKEY_TARGET = 50
export const ONE_TIME_PREKEY_LOW_WATER = 20
/** Подписанный prekey меняется раз в неделю, как рекомендует спецификация X3DH. */
export const SIGNED_PREKEY_ROTATION_MS = 7 * 24 * 60 * 60_000

export type PublishOutcome = 'published' | 'up_to_date' | 'unsupported'

async function toLocalIdentity(record: SecretIdentityRecord): Promise<LocalIdentity> {
  return {
    signing: { publicKey: record.signing_public, privateKey: record.signing_private },
    identity: {
      publicKey: record.identity_public,
      privateKey: await importDhPrivate(record.identity_private),
    },
  }
}

/** Личность устройства: создаётся один раз, приватные ключи из браузера не выносятся. */
export async function ensureLocalIdentity(): Promise<{
  identity: LocalIdentity
  record: SecretIdentityRecord
}> {
  const existing = await secretKeysRepo.identity()
  if (existing) return { identity: await toLocalIdentity(existing), record: existing }
  const signing = await generateSigning()
  const identity = await generateDh(true)
  const record: SecretIdentityRecord = {
    id: 'identity',
    signing_private: signing.privateKey,
    signing_public: signing.publicKey,
    identity_private: await exportDhPrivate(identity.privateKey),
    identity_public: identity.publicKey,
    created_at: new Date().toISOString(),
    published_device_id: null,
    published_at: null,
  }
  await secretKeysRepo.putIdentity(record)
  return { identity: { signing, identity }, record }
}

export async function localIdentity(): Promise<LocalIdentity | null> {
  const record = await secretKeysRepo.identity()
  return record ? await toLocalIdentity(record) : null
}

async function generatePrekey(
  kind: SecretPrekeyRecord['kind'],
  id: number,
): Promise<Omit<SecretPrekeyRecord, 'key'>> {
  const pair = await generateDh(true)
  return {
    kind,
    id,
    private_key: await exportDhPrivate(pair.privateKey),
    public_key: pair.publicKey,
    created_at: new Date().toISOString(),
  }
}

function needsPublish(
  status: SecretKeyStatus,
  record: SecretIdentityRecord,
  deviceId: string,
  signed?: SecretPrekeyRecord,
): boolean {
  if (!status.published) return true
  if (status.signing_key !== toBase64(record.signing_public)) return true
  if (record.published_device_id !== deviceId) return true
  if (!signed || status.signed_prekey_id !== signed.id) return true
  if (Date.now() - Date.parse(signed.created_at) > SIGNED_PREKEY_ROTATION_MS) return true
  return status.one_time_prekeys_available < ONE_TIME_PREKEY_LOW_WATER
}

/**
 * Приводит опубликованный набор в порядок: первая публикация, смена устройства сессии,
 * ротация подписанного prekey и пополнение одноразовых. Приватные части пишутся
 * в базу ДО запроса — сервер не должен раздать ключ, которого у нас ещё нет.
 */
export async function publishDeviceKeys(deviceId: string): Promise<PublishOutcome> {
  if (!(await isSecretCryptoSupported())) return 'unsupported'
  // Две вкладки (или старт приложения и старт чата) не должны генерировать личность дважды.
  return withSecretLock('device-keys', () => publishDeviceKeysLocked(deviceId))
}

/** Фоновая публикация после входа: ошибки сети не мешают приложению, попытка повторится при следующем запуске. */
export async function publishDeviceKeysQuietly(deviceId: string | null | undefined): Promise<void> {
  if (!deviceId) return
  try {
    await publishDeviceKeys(deviceId)
  } catch (error) {
    console.warn('secret keys: публикация отложена', error)
  }
}

async function publishDeviceKeysLocked(deviceId: string): Promise<PublishOutcome> {
  const { identity, record } = await ensureLocalIdentity()
  const status = unwrap(await api.GET('/me/secret-keys'))
  const signedKeys = await secretKeysRepo.prekeys('signed')
  let signed = signedKeys.at(-1)
  if (!needsPublish(status, record, deviceId, signed)) return 'up_to_date'

  let nextId = await secretKeysRepo.nextPrekeyId()
  const fresh: Omit<SecretPrekeyRecord, 'key'>[] = []
  if (!signed || Date.now() - Date.parse(signed.created_at) > SIGNED_PREKEY_ROTATION_MS) {
    const created = await generatePrekey('signed', nextId)
    nextId += 1
    fresh.push(created)
    signed = { ...created, key: `signed:${created.id}` }
  }
  const available = status.published ? status.one_time_prekeys_available : 0
  const oneTime: Omit<SecretPrekeyRecord, 'key'>[] = []
  for (let i = available; i < ONE_TIME_PREKEY_TARGET; i += 1) {
    oneTime.push(await generatePrekey('one_time', nextId))
    nextId += 1
  }
  await secretKeysRepo.putPrekeys([...fresh, ...oneTime])

  const body: SecretKeyBundlePublish = {
    signing_key: toBase64(identity.signing.publicKey),
    identity_key: toBase64(identity.identity.publicKey),
    identity_signature: toBase64(await signIdentity(identity)),
    signed_prekey: {
      id: signed.id,
      key: toBase64(signed.public_key),
      signature: toBase64(await signPrekey(identity, signed.id, signed.public_key)),
    },
    one_time_prekeys: oneTime.map((item) => ({ id: item.id, key: toBase64(item.public_key) })),
  }
  unwrap(await api.PUT('/me/secret-keys', { body }))
  await secretKeysRepo.markPublished(deviceId)
  return 'published'
}
